import { useTranslation } from "@sagot/i18n";
import { Button, CopyButton, Dialog, DialogClose, DialogContent, Icon, Input, toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { useRotateSecret } from "./use-widget-studio";

const K = "deployKnowledge.widgetStudio.rotateDialog";

interface RotateSecretDialogProps {
  agentId: string;
  /** False when no key exists yet (first generation, no grace period copy). */
  configured: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Confirm → `POST …/identity-secret/rotate` → show the returned secret exactly once. The value
 * lives only in component state and is dropped when the dialog closes.
 */
export function RotateSecretDialog({ agentId, configured, open, onOpenChange }: RotateSecretDialogProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const rotate = useRotateSecret(agentId);
  const [secret, setSecret] = useState<string | null>(null);
  const close = (next: boolean) => {
    if (!next) {
      setSecret(null);
      rotate.reset();
    }
    onOpenChange(next);
  };
  const confirm = () => {
    rotate.mutate(undefined, {
      onSuccess: (result) => {
        setSecret(result.secret);
        toast.warning(t(`${K}.rotated`));
      },
      onError: (error) => toast.error(message(error)),
    });
  };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent title={secret ? t(`${K}.newTitle`) : configured ? t(`${K}.title`) : t(`${K}.titleGenerate`)} closeLabel={t("common.close")} className="max-w-md">
        {secret ? (
          <div className="space-y-4">
            <p className="text-sm text-fg-muted">{t(`${K}.newBody`)}</p>
            <div className="flex gap-2">
              <Input readOnly className="font-mono text-xs" value={secret} aria-label={t(`${K}.secretLabel`)} data-testid="new-secret" />
              <CopyButton value={secret} label={t("common.copy")} copiedLabel={t("common.copied")} className="btn-secondary" />
            </div>
            <div className="flex justify-end">
              <Button onClick={() => { close(false); }}>{t(`${K}.done`)}</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {configured ? (
              <div className="flex items-start gap-2.5 rounded-md bg-warning-soft p-3 text-sm">
                <Icon name="alert-triangle" className="mt-0.5 size-4 shrink-0 text-warning" />
                <span>{t(`${K}.warning`)}</span>
              </div>
            ) : null}
            <div className="flex justify-end gap-2">
              <DialogClose asChild>
                <Button variant="ghost">{t("common.cancel")}</Button>
              </DialogClose>
              <Button variant={configured ? "danger" : "primary"} loading={rotate.isPending} onClick={confirm}>
                <Icon name="refresh-cw" />
                {configured ? t(`${K}.confirm`) : t(`${K}.confirmGenerate`)}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
